<template>
  <mobile-responsive-shell :title="title">
    <div class="credit-mobile">
      <div class="credit-mobile-toolbar">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="refresh">
          <i class="bi bi-arrow-clockwise"></i> Actualiser
        </button>
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="print">
          <i class="bi bi-printer"></i> Imprimer
        </button>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          :disabled="selections.length === 0"
          @click="printSelected(selections)"
        >
          <i class="bi bi-printer"></i> Sélection ({{ selections.length }})
        </button>
      </div>

      <p v-if="!credits || credits.length === 0" class="credit-mobile-empty text-muted">
        Aucun crédit à afficher.
      </p>

      <div v-else class="credit-mobile-list">
        <article
          v-for="(row, idx) in credits"
          :key="row.reference || idx"
          class="credit-mobile-card"
        >
          <header class="credit-mobile-card-head">
            <label class="credit-mobile-check">
              <input
                type="checkbox"
                class="form-check-input"
                :value="row.reference"
                v-model="selections"
              />
              <span class="credit-mobile-ref">{{ row.reference }}</span>
            </label>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              @click="viewDetails(row)"
            >
              <i class="bi bi-eye"></i> Détails
            </button>
          </header>

          <p class="credit-mobile-name">
            <i class="bi bi-person text-secondary"></i>
            {{ row.customerFullName || "—" }}
          </p>

          <div class="credit-mobile-amounts">
            <div>
              <span class="credit-mobile-label">Impayé</span>
              <span class="badge bg-danger">
                {{ formatNum(row.totalUnpaid) }}
              </span>
            </div>
            <div>
              <span class="credit-mobile-label">Total TTC</span>
              <span class="badge bg-primary">
                {{ formatNum(row.totalTTC) }}
              </span>
            </div>
          </div>

          <div
            v-if="row.customer && row.customer.maxUnpaid > 0"
            class="credit-mobile-slider"
          >
            <input
              type="range"
              class="form-range"
              :min="0"
              :max="row.customer.maxUnpaid"
              :value="row.totalUnpaid"
              disabled
              :class="{ 'is-invalid': row.totalUnpaid >= row.customer.maxUnpaid }"
            />
            <small class="text-muted">
              {{ formatNum(row.totalUnpaid) }} / {{ formatNum(row.customer.maxUnpaid) }}
            </small>
          </div>

          <footer class="credit-mobile-dates">
            <span><i class="bi bi-calendar-event"></i> Du {{ row.firstDate || "—" }}</span>
            <span><i class="bi bi-calendar-check"></i> Au {{ row.lastDate || "—" }}</span>
          </footer>
        </article>
      </div>

      <div v-if="credits && credits.length" class="credit-mobile-summary">
        <div>
          Σ Impayé <strong>{{ formatNum(totalUnpaidSum) }}</strong>
        </div>
        <div>
          Σ TTC <strong>{{ formatNum(totalTTCSum) }}</strong>
        </div>
      </div>
    </div>
  </mobile-responsive-shell>
</template>

<script>
import { mapGetters } from "vuex";
import MobileResponsiveShell from "../components/MobileResponsiveShell.vue";

export default {
  name: "MobileCreditsListing",
  components: { MobileResponsiveShell },
  data() {
    return {
      title: "Crédits clients",
      selections: [],
    };
  },
  computed: {
    ...mapGetters("credit", {
      credits: "getCredits",
    }),
    totalUnpaidSum() {
      if (!this.credits || !this.credits.length) return 0;
      return this.credits.reduce((a, r) => a + parseFloat(r.totalUnpaid || 0), 0);
    },
    totalTTCSum() {
      if (!this.credits || !this.credits.length) return 0;
      return this.credits.reduce((a, r) => a + parseFloat(r.totalTTC || 0), 0);
    },
  },
  mounted() {
    this.$store.dispatch("credit/getAll");
  },
  methods: {
    formatNum(v) {
      const n = parseFloat(v);
      if (Number.isNaN(n)) return "—";
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    async refresh() {
      await this.$store.dispatch("credit/getAll");
      this.selections = [];
    },
    async print() {
      await this.$store.dispatch("credit/printAll");
    },
    async printSelected(data) {
      await this.$store.dispatch("credit/printAllSelected", data);
    },
    viewDetails(row) {
      this.$router.push({
        name: "reports-customers-index",
        query: {
          customer_reference: row.reference,
          startDate: row.firstDate,
          endtDate: row.lastDate,
        },
      });
    },
  },
};
</script>

<style scoped>
.credit-mobile-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.credit-mobile-empty {
  text-align: center;
  padding: 2rem 1rem;
}

.credit-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.credit-mobile-card {
  background: #fff;
  border-radius: 12px;
  padding: 0.875rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.credit-mobile-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.credit-mobile-check {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  cursor: pointer;
  flex: 1;
  min-width: 0;
}

.credit-mobile-ref {
  font-weight: 700;
  font-size: 1.1rem;
  word-break: break-all;
}

.credit-mobile-name {
  margin: 0 0 0.75rem;
  font-size: 1.05rem;
  line-height: 1.35;
}

.credit-mobile-amounts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  margin-bottom: 0.5rem;
}

.credit-mobile-label {
  display: block;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c757d;
  margin-bottom: 0.15rem;
}

.credit-mobile-slider {
  margin-top: 0.5rem;
}

.credit-mobile-slider .form-range {
  width: 100%;
}

.credit-mobile-dates {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #eee;
  font-size: 0.95rem;
  color: #495057;
}

.credit-mobile-summary {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-radius: 10px;
  font-size: 1.05rem;
}

.form-range.is-invalid::-webkit-slider-thumb {
  background: #dc3545;
}
</style>
